import {useState, useEffect} from 'react';
import {useParams, useNavigate} from 'react-router';
import dayjs from 'dayjs';

import * as OralPhotoApi from '../api/OralPhotoApi';
import PageShell from '../components/PageShell';
import ModelViewer from '../components/ModelViewer';
import './OralPhotos.css';
import {problemMessage} from '../api/client';

const TOKENS = {
  primary: '#C43B71',
  blush: '#FFF0F5',
  surfaceLow: '#F9FAFB',
  ghostBorder: 'rgba(196, 59, 113, 0.16)',
  ink: '#1A1A2E',
  inkMuted: '#6B7280',
};

const FONT = {
  body: 'var(--font-body), Manrope, system-ui',
};

export default function OralPhotosEditor() {
  const {oralPhotoId} = useParams();
  const navigate = useNavigate();

  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [type, setType] = useState('Photo');
  const [fileLink, setFileLink] = useState('');
  const [note, setNote] = useState('');

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    OralPhotoApi.getOralPhotoById(oralPhotoId)
      .then((data) => {
        if (data) {
          setDate(data.shoot_date || dayjs().format('YYYY-MM-DD'));
          setType(data.photo_type || 'Photo');
          setFileLink(data.image_link || '');
          setNote(data.note || '');
        }
      })
      .catch(() => {
        setFormError('Failed to load photo data.');
      });
  }, [oralPhotoId]);

  const validateForm = () => {
    const newErrors = {};
    if (!date) newErrors.date = 'Choose a date';
    if (!type) newErrors.type = 'Choose a type';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');

    if (!validateForm()) return;

    setSubmitting(true);

    const updatedOralPhoto = {
      id: oralPhotoId,
      shoot_date: date,
      photo_type: type,
      image_link: fileLink,
      note: note,
    };

    OralPhotoApi.updateOralPhoto(oralPhotoId, updatedOralPhoto)
      .then(() => {
        navigate('/oral-photo/');
      })
      .catch((err) => {
        setFormError(problemMessage(err, 'Failed to update photo. Please try again.'));
        setSubmitting(false);
      });
  };

  return (
    <PageShell title="Edit item" showBackButton={true}>
      <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column', gap: 20}}>
        <div className="oral-photos-form-card">
          <div style={{display: 'flex', flexDirection: 'column', gap: 16}}>
            <div className="oral-photos-field">
              <label htmlFor="dateInput" className="oral-photos-field-label">
                Date taken
              </label>
              <div className="oral-photos-field-hint">The day it was taken or scanned</div>
              <input
                id="dateInput"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className={`oral-photos-input ${errors.date ? 'invalid' : ''}`}
              />
              {errors.date && <div className="oral-photos-field-error">{errors.date}</div>}
            </div>

            <div className="oral-photos-field">
              <label htmlFor="typeSelect" className="oral-photos-field-label">
                Type
              </label>
              <select
                id="typeSelect"
                value={type}
                onChange={(e) => setType(e.target.value)}
                className={`oral-photos-input ${errors.type ? 'invalid' : ''}`}
              >
                <option value="Photo">Photo</option>
                <option value="X-ray">X-ray</option>
                <option value="3D">3D scan (.stl, .ply)</option>
              </select>
              {errors.type && <div className="oral-photos-field-error">{errors.type}</div>}
            </div>

            {fileLink && (
              <div
                style={{
                  borderRadius: 12,
                  border: `1px solid ${TOKENS.ghostBorder}`,
                  overflow: 'hidden',
                  background: TOKENS.blush,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 16,
                }}
              >
                {type === '3D' ? (
                  <ModelViewer
                    url={fileLink}
                    fileType={fileLink.toLowerCase().includes('.ply') ? 'ply' : 'stl'}
                  />
                ) : (
                  <img
                    src={fileLink}
                    alt="Preview"
                    style={{
                      maxHeight: 300,
                      display: 'block',
                      maxWidth: '100%',
                      objectFit: 'contain',
                      borderRadius: 8,
                    }}
                  />
                )}
              </div>
            )}

            <div className="oral-photos-field">
              <label htmlFor="noteInput" className="oral-photos-field-label">
                Note
              </label>
              <textarea
                id="noteInput"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add any notes about this file (optional)"
                className={`oral-photos-textarea ${errors.note ? 'invalid' : ''}`}
              />
              {errors.note && <div className="oral-photos-field-error">{errors.note}</div>}
            </div>
          </div>
        </div>

        {formError && (
          <div
            role="alert"
            style={{
              padding: 16,
              borderRadius: 12,
              background: '#FEF2F2',
              border: '1px solid #DC2626',
              color: '#B91C1C',
              fontFamily: FONT.body,
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {formError}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="oral-photos-add-btn"
          style={{justifyContent: 'center', width: '100%', height: 56, fontSize: 16}}
        >
          {submitting ? 'Saving...' : 'Save changes'}
        </button>

        <button
          type="button"
          onClick={() => navigate('/oral-photo/')}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontFamily: FONT.body,
            fontSize: 15,
            fontWeight: 700,
            color: TOKENS.primary,
            padding: '12px',
            marginTop: '-8px',
          }}
        >
          Cancel
        </button>
      </form>
    </PageShell>
  );
}
